import React, { useState } from "react";

import { FilterIcon as Filter } from "@packages/ds";
import { useIntl } from "react-intl";

import { THIRD_PARTIES, ThirdParty } from "Constants/integrationThirdParties";
import { CloseButton } from "ds/Button";
import * as Dropdown from "ds/Dropdown";
import useInput from "Hooks/useInput";
import { selectFeatureObservabilitySuiteIntegrationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useObservabilitySuiteIntegrationList } from "src/organization/pages/project/settings/integrations/components/IntegrationForm/config";
import IntegrationCategory from "src/organization/pages/project/settings/integrations/Setup/components/IntegrationCategory";
import { useAppSelector } from "Store/hooks";

import * as S from "./IntegrationsList.styles";

const IntegrationsList = () => {
  const observabilitySuiteIntegrationsEnabled = useAppSelector(
    selectFeatureObservabilitySuiteIntegrationsEnabled
  );
  const [openIntegrationsModal, setOpenIntegrationsModal] = useState<boolean>();
  const { integrationConfig, getIntegrationEnabled } =
    useObservabilitySuiteIntegrationList();

  const categories = THIRD_PARTIES.reduce<string[]>((acc, cu) => {
    const cat = cu.category;
    if (!acc.includes(cat)) acc.push(cat);
    return acc;
  }, []).concat(integrationConfig.map(nex => nex.category));

  const integrationConfigByCategory = integrationConfig.reduce<
    Record<string, ThirdParty[]>
  >((cfgs, next) => {
    if (cfgs[next.category]) return cfgs;
    const catConfigs = integrationConfig.filter(
      cfg => cfg.category === next.category
    );
    cfgs[next.category] = catConfigs;
    return cfgs;
  }, {});

  const { inputs, handleInputChange, setInputs } = useInput(
    categories.reduce<Record<string, boolean>>((acc, cu) => {
      acc[cu] = true;
      return acc;
    }, {})
  );

  const [search, setSearch] = useState<string>();
  const intl = useIntl();

  const handleChange = (value: string) => {
    setSearch(value);
  };

  const getItems = (cat: string) => {
    return THIRD_PARTIES.filter(elt => elt.category === cat);
  };

  const getCategories = () => {
    return categories.map(cat => {
      let items = getItems(cat);

      if (search) {
        items = items.filter(elt => {
          const formattedName = intl
            .formatMessage({
              id: `integration.type.${elt.type}`
            })
            .toLowerCase();
          return formattedName.includes(search.toLowerCase());
        });
      }
      return (
        <React.Fragment key={`cat-${cat}`}>
          {!!items.length && inputs[cat] && cat !== "custom" && (
            <IntegrationCategory name={cat} list={items} />
          )}

          {cat == "custom" && (
            <>
              {observabilitySuiteIntegrationsEnabled &&
                Object.keys(integrationConfigByCategory).map(
                  (categoryName, i) => {
                    const categoryConfigs = searchObservabilityIntegrations(
                      integrationConfigByCategory[categoryName]
                    );
                    return (
                      inputs[categoryName] &&
                      !!categoryConfigs.length && (
                        <IntegrationCategory
                          key={`cat-${cat}-log-forwarding-${i}`}
                          getEnablement={category =>
                            getIntegrationEnabled(category.type)
                          }
                          name={categoryName}
                          list={categoryConfigs}
                        />
                      )
                    );
                  }
                )}
              {!!items.length && inputs[cat] && (
                <IntegrationCategory
                  key={`cat-${cat}-custom`}
                  name={cat}
                  list={items}
                />
              )}
            </>
          )}
        </React.Fragment>
      );
    });
  };

  const searchObservabilityIntegrations = (cfgs: ThirdParty[]) => {
    const lowerCaseSearch = search?.toLowerCase();
    if (lowerCaseSearch)
      return cfgs.filter(
        i =>
          i.category.toLowerCase().includes(lowerCaseSearch) ||
          i.description.toLowerCase().includes(lowerCaseSearch) ||
          i.info?.description.toLowerCase().includes(lowerCaseSearch) ||
          i.info?.description.toLowerCase().includes(lowerCaseSearch)
      );
    return cfgs;
  };

  const selectAllCat = () => {
    for (const [key, value] of Object.entries(inputs)) {
      if (!value) {
        setInputs(inputs => ({ ...inputs, [key]: true }));
      }
      if (Object.entries(inputs).every(elt => elt[1])) {
        setInputs(inputs => ({ ...inputs, [key]: false }));
      }
    }
  };

  return (
    <S.Wrapper>
      <S.Header>
        <S.SearchInput
          placeholder={intl.formatMessage({
            id: "integration.setup.categories.search.placeholder",
            defaultMessage: "Search integration"
          })}
          value={search}
          onChange={handleChange}
        />

        <Dropdown.Root
          id="integration.setup.categories.filter"
          open={openIntegrationsModal}
          onOpenChange={() => setOpenIntegrationsModal(!openIntegrationsModal)}
        >
          <S.DropdownTrigger variant="mixed">
            {intl.formatMessage({
              id: "integration.setup.categories.filter",
              defaultMessage: "Filter"
            })}{" "}
            <Filter />
          </S.DropdownTrigger>
          <Dropdown.Portal>
            <Dropdown.Content align="end">
              <S.DropdownHeader>
                <Dropdown.Label>
                  {intl.formatMessage({
                    id: "integration.setup.categories.label",
                    defaultMessage: "Filter integrations"
                  })}
                </Dropdown.Label>
                <CloseButton onClick={() => setOpenIntegrationsModal(false)} />
              </S.DropdownHeader>
              <Dropdown.Separator />

              <Dropdown.CheckboxItem
                checked={Object.entries(inputs).every(elt => elt[1])}
                onCheckedChanged={() => selectAllCat()}
              >
                {intl.formatMessage({
                  id: `integration.setup.categories.all`
                })}
              </Dropdown.CheckboxItem>

              {categories.map(cat => (
                <Dropdown.CheckboxItem
                  key={`cat-${cat}-dropdown`}
                  checked={inputs[cat]}
                  onCheckedChanged={checked =>
                    handleInputChange({
                      target: {
                        type: "checkbox",
                        checked,
                        name: cat
                      }
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                >
                  {intl.formatMessage({
                    id: `integration.setup.categories.${cat}.title`
                  })}
                </Dropdown.CheckboxItem>
              ))}
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </S.Header>
      {getCategories()}
    </S.Wrapper>
  );
};

export default IntegrationsList;
